"use client";

import { useState } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import SectionTitle from "@/components/SectionTitle";

interface FloorPlanSlide {
  image: string;
  title: string;
  description: string;
}

interface FloorPlanSectionProps {
  slides: FloorPlanSlide[];
  title: string;
}

export default function FloorPlanSection({
  slides,
  title,
}: FloorPlanSectionProps) {
  const [current, setCurrent] = useState(0);

  if (!slides || slides.length === 0) return null;

  const slide = slides[current];
  const total = slides.length;
  const next = () => setCurrent((i) => (i + 1) % total);

  return (
    <section
      id="mat-bang"
      className="relative w-full h-auto lg:h-screen py-12 md:py-16 lg:py-0 flex flex-col border-t border-black/5 overflow-hidden"
      style={{
        background:
          "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 1) 100%)",
      }}
    >
      {/* Full-width bg strip — bottom 1/3 of the section */}
      <div className="absolute bottom-0 left-0 right-0 w-full bg-[#F3F0EB] pointer-events-none h-[33.333%] lg:h-[33.333vh]" />

      {/* ── TOP: Text header ── */}
      <div className="relative z-10 flex-none max-w-7xl w-full mx-auto px-6 md:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start">
          {/* Left: label + title */}
          <div className="md:col-span-5">
            <SectionTitle
              title={slide.title}
              subtitle={title}
              align="left"
              theme="green"
              subtitleColor="primaryHover"
              underlineColor="primaryHover"
            />
          </div>

          {/* Right: description + slide counter */}
          <div className="md:col-span-7 flex flex-col justify-end gap-3">
            <p className="text-sm text-black/65 leading-relaxed font-semibold text-justify">
              {slide.description}
            </p>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-black text-primary tabular-nums">
                {String(current + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 h-[1px] bg-black/10" />
              <span className="text-sm font-bold text-black/35 tabular-nums">
                {String(total).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── BOTTOM: Image + Next button column ── */}
      <div className="relative z-10 flex-1 flex min-h-0 max-w-7xl w-full mx-auto px-6 md:px-8 pb-8 gap-0">
        <div className="relative flex-1 rounded-xl group aspect-[1.6/1] lg:aspect-auto">
          <SmoothImage
            key={current}
            src={slide.image}
            alt={slide.title}
            fill
            priority
            sizes="(min-width: 1280px) 1200px, 95vw"
            className="object-contain transition-transform duration-700 group-hover:scale-[1.015] filter drop-shadow-2xl"
          />

          {/* Bottom gradient */}
          {/* <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" /> */}

          {/* Dot indicators */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrent(i);
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === current
                    ? "w-8 bg-[#B39775]"
                    : "w-2 bg-black/50 hover:bg-black/80"
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Next button — narrow right column, bottom-aligned, outside image */}
        <div className="flex-none w-16 flex items-end justify-center pb-1">
          <button
            onClick={next}
            className="w-12 h-12 rounded-lg bg-[#1C1714] text-white flex items-center justify-center hover:bg-[#B39775] transition-all duration-300 shadow-lg"
            aria-label="Ảnh tiếp theo"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
